<template>
  <div class="permission-selector">
    <!-- Global Select All -->
    <div
      v-if="!readonly"
      class="d-flex align-items-center mb-1 mt-4"
    >
      <b-form-checkbox
        :checked="allPermissionsSelected"
        :indeterminate="somePermissionsSelected && !allPermissionsSelected"
        @change="toggleAllPermissions"
      >
        <strong>{{ $t('Select All Permissions') }}</strong>
      </b-form-checkbox>
      <small
        v-if="allPermissionNames.length"
        class="text-muted ml-1"
      >
        ({{ selectedPermissionCount }}/{{ allPermissionNames.length }})
      </small>
    </div>

    <!-- Permission Search -->
    <PermissionSearch
      v-model="searchTerm"
      :placeholder="$t('Search permissions...')"
      :result-count="searchTerm ? Object.keys(filteredPermissions).length : null"
    />

    <!-- Permission Accordion -->
    <div class="business-card mt-1">
      <app-collapse
        class="business-items"
        type="margin"
        accordion
      >
        <app-collapse-item
          v-for="(item, key) in filteredPermissions"
          :key="key"
          :title="key"
          class="card-item px-2"
        >
          <b-row class="ml-0">
            <!-- Select All -->
            <b-col
              md="3"
              class="business-item mr-1 mb-1"
            >
              <div class="d-flex align-items-center justify-content-between">
                <b-form-checkbox
                  :checked="allSelected[key] || false"
                  :aria-describedby="key"
                  :aria-controls="key"
                  :disabled="readonly"
                  @change="toggleAll(item.slice(1), $event, key)"
                >
                  {{ $t("Select All") }}
                </b-form-checkbox>
              </div>
            </b-col>
            <!-- Individual Permissions -->
            <b-col
              v-for="(permission, j) in item.slice(1)"
              :key="j"
              md="3"
              class="business-item mr-1 mb-1"
            >
              <div class="d-flex align-items-center justify-content-between">
                <b-form-checkbox
                  :checked="value.includes(permission.name)"
                  :disabled="readonly"
                  @change="togglePermission(permission.name, $event)"
                >
                  {{ permission.description }}
                </b-form-checkbox>
              </div>
            </b-col>
          </b-row>
        </app-collapse-item>
      </app-collapse>
    </div>
  </div>
</template>

<script>
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import { BCol, BFormCheckbox, BRow } from 'bootstrap-vue'
import PermissionSearch from './PermissionSearch.vue'

export default {
  name: 'PermissionSelector',
  components: {
    BRow,
    BCol,
    BFormCheckbox,
    AppCollapse,
    AppCollapseItem,
    PermissionSearch,
  },
  props: {
    // v-model for selected permissions array
    value: {
      type: Array,
      default: () => [],
    },
    // Full permissions object grouped by category
    permissions: {
      type: Object,
      default: () => ({}),
    },
    // Disable editing
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchTerm: '',
      allSelected: {},
    }
  },
  computed: {
    allPermissionNames() {
      const names = []
      Object.values(this.permissions).forEach(group => {
        group.forEach(permission => {
          if (permission && permission.name) {
            names.push(permission.name)
          }
        })
      })
      return names
    },
    selectedPermissionCount() {
      const nameSet = new Set(this.allPermissionNames)
      return this.value.filter(name => nameSet.has(name)).length
    },
    allPermissionsSelected() {
      return this.allPermissionNames.length > 0
        && this.selectedPermissionCount === this.allPermissionNames.length
    },
    somePermissionsSelected() {
      return this.selectedPermissionCount > 0
    },
    sortedPermissions() {
      return Object.fromEntries(Object.entries(this.permissions).sort())
    },
    filteredPermissions() {
      const search = this.searchTerm.toLowerCase().trim()
      if (!search) {
        return this.sortedPermissions
      }

      const filtered = {}
      Object.entries(this.sortedPermissions).forEach(([groupKey, groupPermissions]) => {
        const groupMatches = groupKey.toLowerCase().includes(search)
        const matchingPermissions = groupPermissions.slice(1).filter(permission => {
          const nameMatches = permission.name?.toLowerCase().includes(search)
          const descMatches = permission.description?.toLowerCase().includes(search)
          return nameMatches || descMatches
        })

        if (groupMatches || matchingPermissions.length > 0) {
          filtered[groupKey] = groupMatches
            ? groupPermissions
            : [undefined, ...matchingPermissions]
        }
      })

      return filtered
    },
  },
  watch: {
    value: {
      handler() {
        this.updateSelectAllStates()
      },
      deep: true,
    },
    permissions: {
      handler() {
        this.updateSelectAllStates()
      },
      deep: true,
    },
  },
  mounted() {
    this.updateSelectAllStates()
  },
  methods: {
    toggleAllPermissions(checked) {
      if (checked) {
        const newValue = [...new Set([...this.value, ...this.allPermissionNames])]
        this.$emit('input', newValue)
      } else {
        const removeSet = new Set(this.allPermissionNames)
        this.$emit('input', this.value.filter(name => !removeSet.has(name)))
      }
    },
    togglePermission(permissionName, checked) {
      const newValue = [...this.value]
      if (checked) {
        if (!newValue.includes(permissionName)) {
          newValue.push(permissionName)
        }
      } else {
        const index = newValue.indexOf(permissionName)
        if (index > -1) {
          newValue.splice(index, 1)
        }
      }
      this.$emit('input', newValue)
    },
    toggleAll(arr, checked, key) {
      const newValue = [...this.value]
      if (checked) {
        arr.forEach(element => {
          if (!newValue.includes(element.name)) {
            newValue.push(element.name)
          }
        })
      } else {
        arr.forEach(element => {
          const index = newValue.indexOf(element.name)
          if (index > -1) {
            newValue.splice(index, 1)
          }
        })
      }
      this.allSelected = { ...this.allSelected, [key]: checked }
      this.$emit('input', newValue)
    },
    updateSelectAllStates() {
      if (!this.permissions || Object.keys(this.permissions).length === 0) {
        return
      }

      const newAllSelected = {}
      Object.keys(this.permissions).forEach(groupKey => {
        const groupPermissions = this.permissions[groupKey]
          .filter(permission => permission && permission.name)

        if (groupPermissions.length === 0) {
          newAllSelected[groupKey] = false
          return
        }

        const selectedCount = groupPermissions.filter(
          permission => this.value && this.value.includes(permission.name),
        ).length

        newAllSelected[groupKey] = selectedCount === groupPermissions.length
      })

      this.allSelected = { ...newAllSelected }
    },
    clearSearch() {
      this.searchTerm = ''
    },
  },
}
</script>

<style lang="scss" scoped>
.card-item {
  border: 1px solid #d8d6de !important;
  border-radius: 0.358rem;
}
</style>
